import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  MessageCircle, X, Send, Paperclip, Users, User, 
  ChevronUp, ChevronDown, Search, Plus, Hash, 
  Headphones, Building2, Monitor, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Default chat templates
const DEFAULT_TEMPLATES = [
  { id: 1, name: 'Welcome Message', category: 'onboarding', content: 'Welcome to JustVerified! We are here to assist you with your background verification process.' },
  { id: 2, name: 'Document Request', category: 'verification', content: 'We require additional documents for your verification. Please upload the following documents at your earliest convenience.' },
  { id: 3, name: 'Verification Complete', category: 'completion', content: 'Your background verification has been completed successfully. Thank you for your cooperation.' },
  { id: 4, name: 'Insufficiency Notice', category: 'insufficiency', content: 'We found some discrepancies in your submitted documents. Please provide clarification at your earliest.' },
  { id: 5, name: 'Follow-up Reminder', category: 'reminder', content: 'This is a friendly reminder to submit your pending documents for the verification process.' },
];

const ChatTypeIcon = ({ type }) => {
  switch(type) {
    case 'group': return <Users className="w-4 h-4" />;
    case 'support': return <Headphones className="w-4 h-4" />;
    case 'client': return <Building2 className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

export default function SmartChat({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('chats'); // chats, channels, contacts
  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatTemplates, setChatTemplates] = useState(DEFAULT_TEMPLATES);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      fetchRooms();
      fetchContacts();
      fetchUnreadCount();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    // Poll for new messages every 5 seconds
    const interval = setInterval(() => {
      if (isOpen && !isMinimized) {
        fetchUnreadCount();
        if (selectedRoom) {
          fetchMessages(selectedRoom.id);
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen, isMinimized, selectedRoom]);

  useEffect(() => {
    // Refresh contacts every 60s to keep presence dots up-to-date
    if (!isOpen || isMinimized) return;
    const interval = setInterval(() => {
      fetchContacts();
    }, 60000);
    return () => clearInterval(interval);
  }, [isOpen, isMinimized]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch templates from backend (with fallback to defaults)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get('/chat/templates');
        if (response.data && response.data.length > 0) {
          setChatTemplates(response.data);
        }
      } catch (error) {
        // Use default templates if backend doesn't have any
        console.log('Using default templates');
      }
    };
    fetchTemplates();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUseTemplate = (template) => {
    setNewMessage(template.content);
    setShowTemplates(false);
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get('/chat/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get('/chat/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get('/chat/unread-count');
      setUnreadTotal(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectRoom = async (room) => {
    setSelectedRoom(room);
    await fetchMessages(room.id);
    // Refresh rooms to update unread counts
    fetchRooms();
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    try {
      await axios.post(`/chat/rooms/${selectedRoom.id}/messages`, {
        content: newMessage,
        message_type: 'text'
      });
      setNewMessage('');
      await fetchMessages(selectedRoom.id);
      fetchRooms();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedRoom) return;

    // For now, simulate file upload - in production, upload to cloud storage
    const fileUrl = URL.createObjectURL(file);
    
    try {
      await axios.post(`/chat/rooms/${selectedRoom.id}/messages`, {
        content: `Shared file: ${file.name}`,
        message_type: 'file',
        file_name: file.name,
        file_url: fileUrl
      });
      await fetchMessages(selectedRoom.id);
      fetchRooms();
      toast.success('File shared');
    } catch (error) {
      toast.error('Failed to share file');
    }
  };

  const handleStartChat = async (contact) => {
    try {
      const response = await axios.post('/chat/rooms', {
        name: contact.name,
        type: 'direct',
        participants: [contact.id]
      });
      setSelectedRoom(response.data);
      await fetchMessages(response.data.id);
      fetchRooms();
      setShowNewChat(false);
      setActiveTab('chats');
    } catch (error) {
      toast.error('Failed to start chat');
    }
  };

  const handleScreenShare = () => {
    toast.info('Screen sharing would open a video call window (Feature coming soon)');
  };

  const initializeChannels = async () => {
    try {
      await axios.post('/chat/init-channels');
      toast.success('Chat channels initialized');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to initialize channels');
    }
  };

  const filteredRooms = rooms.filter(room => 
    room.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const directChats = filteredRooms.filter(r => r.type === 'direct');
  const groupChats = filteredRooms.filter(r => r.type !== 'direct');

  const getRoleBadgeColor = (role) => {
    const colors = {
      'agency_admin': 'bg-green-100 text-green-700',
      'hr_admin': 'bg-blue-100 text-blue-700',
      'qc_operations': 'bg-purple-100 text-purple-700',
      'verification_team': 'bg-orange-100 text-orange-700',
      'agent_vendor': 'bg-amber-100 text-amber-700',
      'employee': 'bg-teal-100 text-teal-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  // Floating chat button when closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full w-12 h-12 bg-emerald-600 hover:bg-emerald-700 shadow-lg"
          data-testid="open-chat-btn"
        >
          <MessageCircle className="w-5 h-5" />
          {unreadTotal > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadTotal > 9 ? '9+' : unreadTotal}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <div 
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 shadow-lg"
          onClick={() => setIsMinimized(false)}
        >
          <MessageCircle className="w-4 h-4" />
          <span className="font-medium">Smart Chat</span>
          {unreadTotal > 0 && (
            <Badge className="bg-red-500 text-white text-xs">{unreadTotal}</Badge>
          )}
          <ChevronUp className="w-4 h-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-[380px] h-[500px] bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col">
      {/* Header */}
      <div className="bg-indigo-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <span className="font-semibold">Smart Chat</span>
          <span className="text-xs opacity-75">(Ctrl+Space)</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="text-white hover:bg-indigo-700 p-1" onClick={() => setIsMinimized(true)}>
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-indigo-700 p-1" onClick={() => setIsOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {selectedRoom ? (
        // Chat View
        <div className="flex-1 flex flex-col">
          {/* Room Header */}
          <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="p-1" onClick={() => setSelectedRoom(null)}>
                <ChevronDown className="w-4 h-4 rotate-90" />
              </Button>
              <ChatTypeIcon type={selectedRoom.type} />
              <span className="font-medium text-sm">{selectedRoom.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="p-1" onClick={handleScreenShare} title="Screen Share">
                <Monitor className="w-4 h-4 text-slate-600" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-3">
              {messages.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-8">No messages yet. Start the conversation!</p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${msg.sender_id === user.id ? 'bg-indigo-600 text-white' : 'bg-slate-100'} rounded-lg px-3 py-2`}>
                      {msg.sender_id !== user.id && (
                        <p className="text-xs font-medium text-indigo-600 mb-1">{msg.sender_name}</p>
                      )}
                      {msg.message_type === 'file' ? (
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4" />
                          <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="text-sm underline">
                            {msg.file_name}
                          </a>
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                      <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-indigo-200' : 'text-slate-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-3 border-t bg-white">
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                className="p-2"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
              >
                <Paperclip className="w-4 h-4 text-slate-500" />
              </Button>
              
              {/* Template Selector */}
              <Popover open={showTemplates} onOpenChange={setShowTemplates}>
                <PopoverTrigger asChild>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="p-2"
                    title="Use template"
                  >
                    <FileText className="w-4 h-4 text-slate-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2" align="start" side="top">
                  <p className="text-xs font-semibold text-slate-500 px-2 py-1 mb-1">Quick Templates</p>
                  <ScrollArea className="h-48">
                    <div className="space-y-1">
                      {chatTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => handleUseTemplate(template)}
                          className="w-full text-left px-2 py-2 rounded hover:bg-slate-100 transition-colors"
                        >
                          <p className="font-medium text-sm text-slate-700">{template.name}</p>
                          <p className="text-xs text-slate-500 truncate">{template.content.substring(0, 50)}...</p>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>

              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 text-sm"
              />
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      ) : (
        // Room List View
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('chats')}
              className={`flex-1 py-2 text-sm font-medium ${activeTab === 'chats' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            >
              Chats
            </button>
            <button
              onClick={() => setActiveTab('channels')}
              className={`flex-1 py-2 text-sm font-medium ${activeTab === 'channels' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            >
              Channels
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 py-2 text-sm font-medium ${activeTab === 'contacts' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            >
              Contacts
            </button>
          </div>

          {/* Search */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="pl-8 text-sm h-8"
              />
            </div>
          </div>

          <ScrollArea className="flex-1">
            {activeTab === 'chats' && (
              <div className="p-2">
                {/* Unread Section */}
                {directChats.filter(r => r.my_unread > 0).length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-amber-600 px-2 py-1 bg-amber-50 rounded mb-2">
                      Unread Chats
                    </p>
                    {directChats.filter(r => r.my_unread > 0).map(room => (
                      <div
                        key={room.id}
                        onClick={() => handleSelectRoom(room)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{room.name}</p>
                          <p className="text-xs text-slate-500 truncate">{room.last_message_preview || 'No messages'}</p>
                        </div>
                        <Badge className="bg-red-500 text-white text-xs">{room.my_unread}</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {/* All Chats */}
                <p className="text-xs font-semibold text-slate-500 px-2 mb-2">Direct Messages</p>
                {directChats.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No chats yet</p>
                ) : (
                  directChats.filter(r => r.my_unread === 0).map(room => (
                    <div
                      key={room.id}
                      onClick={() => handleSelectRoom(room)}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{room.name}</p>
                        <p className="text-xs text-slate-500 truncate">{room.last_message_preview || 'No messages'}</p>
                      </div>
                    </div>
                  ))
                )}

                {/* New Chat Button */}
                <Button
                  onClick={() => setActiveTab('contacts')}
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              </div>
            )}

            {activeTab === 'channels' && (
              <div className="p-2">
                <p className="text-xs font-semibold text-slate-500 px-2 mb-2">Channels</p>
                {groupChats.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-400 mb-3">No channels yet</p>
                    {user.role === 'agency_admin' && (
                      <Button onClick={initializeChannels} variant="outline" size="sm">
                        Initialize Channels
                      </Button>
                    )}
                  </div>
                ) : (
                  groupChats.map(room => (
                    <div
                      key={room.id}
                      onClick={() => handleSelectRoom(room)}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <ChatTypeIcon type={room.type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{room.name}</p>
                        <p className="text-xs text-slate-500 truncate">{room.last_message_preview || 'No messages'}</p>
                      </div>
                      {room.my_unread > 0 && (
                        <Badge className="bg-red-500 text-white text-xs">{room.my_unread}</Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'contacts' && (
              <div className="p-2" data-testid="smart-chat-contacts-list">
                {contacts.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No contacts found</p>
                ) : (() => {
                  const filtered = contacts.filter(c =>
                    (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (c.email || '').toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  const INTERNAL_ROLES = new Set([
                    'agency_admin', 'operations_manager', 'qc_operations',
                    'data_entry_team', 'inhouse_verification', 'vendor_management',
                    'verification_team', 'agent_vendor', 'vendor', 'external_vendor',
                    'b2b_admin'
                  ]);
                  const CLIENT_ROLES = new Set(['hr_admin', 'b2b_client']);
                  const CANDIDATE_ROLES = new Set(['employee']);
                  const groups = [
                    { key: 'internal', label: 'Internal Team', items: filtered.filter(c => INTERNAL_ROLES.has(c.role)) },
                    { key: 'clients',  label: 'Clients',       items: filtered.filter(c => CLIENT_ROLES.has(c.role)) },
                    { key: 'candidates', label: 'Candidates',  items: filtered.filter(c => CANDIDATE_ROLES.has(c.role)) },
                  ].filter(g => g.items.length > 0);

                  if (groups.length === 0) {
                    return <p className="text-sm text-slate-400 text-center py-4">No matches</p>;
                  }

                  return groups.map(group => (
                    <div key={group.key} className="mb-3" data-testid={`smart-chat-group-${group.key}`}>
                      <div className="flex items-center justify-between px-2 mb-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {group.label}
                        </p>
                        <span className="text-[11px] text-slate-400 font-medium">{group.items.length}</span>
                      </div>
                      {group.items.map(contact => {
                        const isOnline = !!contact.online;
                        let presenceText = 'Offline';
                        if (isOnline) {
                          presenceText = 'Active now';
                        } else if (contact.last_active_at) {
                          try {
                            const last = new Date(contact.last_active_at);
                            const mins = Math.floor((Date.now() - last.getTime()) / 60000);
                            if (mins < 60) presenceText = `Active ${mins}m ago`;
                            else if (mins < 1440) presenceText = `Active ${Math.floor(mins / 60)}h ago`;
                            else presenceText = `Active ${Math.floor(mins / 1440)}d ago`;
                          } catch { /* keep default */ }
                        }
                        return (
                          <div
                            key={contact.id}
                            onClick={() => handleStartChat(contact)}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                            data-testid={`smart-chat-contact-${contact.id}`}
                          >
                            <div className="relative">
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-600" />
                              </div>
                              <span
                                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${
                                  isOnline ? 'bg-emerald-500' : 'bg-slate-300'
                                }`}
                                data-testid={`smart-chat-presence-${contact.id}`}
                                title={presenceText}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{contact.name}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {isOnline ? (
                                  <span className="text-emerald-600 font-medium">{presenceText}</span>
                                ) : (
                                  contact.email
                                )}
                              </p>
                            </div>
                            <Badge className={`text-xs ${getRoleBadgeColor(contact.role)}`}>
                              {contact.role?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
